// src/ui/App.tsx — top-level React composition.
//
// Stage machine is owned by useGateAuth:
//   loading → solid dark blank (no flash of gate before bootstrap probe lands)
//   gate    → GateScreen (password entry; POSTs /api/auth)
//   splash  → BootSplash (1.2s min hold, 300ms fade)
//   game    → CharacterSelect (UI-02) → PhaserGame + InstallHint
//
// P5 Plan 06 (D-24): useContent() hydrates the sim content cache before the
// game subtree mounts. The boot sequence is:
//   gate → splash → (useContent().ready === true) → CharacterSelect → game subtree.
// If the 1.2s splash hold ends before content is hydrated, we keep a
// BootSplash mounted in the interim so there is no white-screen gap. On a
// warm SW cache this window is effectively zero; on cold/offline boot the
// useContent fallback path (hydrateContent(DEFAULT_BUNDLE)) guarantees we
// still reach ready:true in a handful of ms — boot cannot stall (T-05-24).
//
// P5 Plan 07 (D-26..D-30, UI-02): CharacterSelect mounts after content.ready
// and before the PhaserGame subtree. The chosenCharacterId state lifts here
// to gate the subtree — the game does not boot until a character is picked.
// The CharacterSelect confirm handler also writes
// sessionStorage['hunter:dev.character'] which GameScene reads unconditionally
// at scene init (production propagation path).
//
// PreviewBanner mounts at the top of the fragment so it appears in every
// stage when ?preview=draft is active. It self-gates via isPreviewMode()
// (Pitfall 9 self-healing), so mounting it unconditionally is safe.
//
// Overlays (VersionOverlay, DevOverlay, UpdateToast) mount in every stage so
// a backgrounded session can still surface the "update available" toast when
// it comes back to the gate.
//
// P5 Plan 05-11: admin authentication is now a client-side gate mirroring
// the game path. /admin visits route through <AdminAppGated/>, which renders
// <GateScreen mode='admin'/> until the hunter-admin-session cookie is
// verified via a probe to /api/admin/content/history. On 200 the gate
// unmounts and AdminApp renders (no BootSplash for admins — the splash
// animation is nostalgia-coded for players, not makers). Defense-in-depth
// only: the authoritative gate remains server-side middleware (T-05-G11-01).

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from 'react';

import { PhaserGame } from '../render/PhaserGame';
import { audioManager } from '../render/audio/manager';
import { P6_UNLOCK_BOOT_PROBE_MS } from '../render/audio/constants';
import { renderBus } from '../render/bus';
import type { EntityStatus } from '../shared/types/world';

// P5 Plan 08: admin SPA lives on its own route + is code-split so the player
// bundle never pays the Zod-authoring-UI cost. scripts/check-admin-split.mjs
// is the CI regression guard.
const AdminApp = lazy(() => import('./admin/AdminApp'));

import { HideExitButton } from './controls/HideExitButton';
import { Joystick } from './controls/Joystick';
import { OnFloorDropButton } from './controls/OnFloorDropButton';
import { TorchButton } from './controls/TorchButton';
import { AudioUnlockSplash } from './gate/AudioUnlockSplash';
import { GateScreen } from './gate/GateScreen';
import { useGateAuth } from './gate/useGateAuth';
import { BubbleLayer } from './bubbles/BubbleLayer';
import { FearFeedbackLayer } from './fear-feedback/FearFeedbackLayer';
import { DevOverlay } from './overlays/DevOverlay';
import { HeadstartCountdown } from './overlays/HeadstartCountdown';
import { InstallHint } from './overlays/InstallHint';
import { RoundTimer } from './overlays/RoundTimer';
import { StaminaBar } from './overlays/StaminaBar';
import { TeamSwitchOverlay } from './overlays/TeamSwitchOverlay';
import { UpdateToast } from './overlays/UpdateToast';
import { VersionOverlay } from './overlays/VersionOverlay';
import { PreviewBanner } from './preview-banner';
import { BootSplash } from './splash/BootSplash';
import { CharacterSelect } from './screens/CharacterSelect';
import { TitleScreen } from './screens/TitleScreen';
import { RoundSetup } from './screens/RoundSetup';
import { ControlsTutorialCard } from './overlays/ControlsTutorialCard';
import { RoundResultCard } from './overlays/RoundResultCard';
import { SettingsPanel } from './overlays/SettingsPanel';
import { SpectatorBanner } from './overlays/SpectatorBanner';
import { PauseButton } from './controls/PauseButton';
import { loadSettings } from './settings/store';
import { useContent } from './useContent';

export function App(): JSX.Element {
  // P5 Plan 08 + Plan 11: /admin route short-circuits the player boot sequence.
  // Checked at render time (not via React Router per D-13). Admin auth is
  // still gated server-side by functions/_middleware.ts as the authoritative
  // line; the client gate below is defense-in-depth so the admin UI does
  // not flash for unauthenticated users (T-05-G11-01).
  //
  // The admin branch delegates to a dedicated component so the player
  // sub-tree's hooks (useGateAuth / useContent / useState) are NOT called
  // on the admin path — React's rules-of-hooks forbids conditional hook
  // invocation, so we route BEFORE any hook runs.
  const isAdminRoute =
    typeof window !== 'undefined' &&
    typeof window.location?.pathname === 'string' &&
    window.location.pathname.startsWith('/admin');
  if (isAdminRoute) {
    return <AdminAppGated />;
  }
  return <PlayerApp />;
}

function AdminAppGated(): JSX.Element {
  // Admin variant of the bootstrap hook: probe /api/admin/content/history
  // and POST { password, role: 'admin' } on login. Stage 'splash' is treated
  // as "mount AdminApp" — admins skip the nostalgic BootSplash (it's player
  // content). We OR in 'game' as a belt-and-braces guard in case a future
  // call site calls finishSplash(); today the admin machine settles in
  // 'splash' on success and never advances further.
  const { stage, error, login } = useGateAuth({ role: 'admin' });
  return (
    <>
      {/* PreviewBanner kept on every admin stage for parity with player tree —
       *  it self-gates on isPreviewMode() so it is inert unless
       *  ?preview=draft is set. */}
      <PreviewBanner />
      {stage === 'loading' && (
        <div
          data-testid="admin-loading-blank"
          style={{ background: '#0d1f2d', position: 'fixed', inset: 0 }}
        />
      )}
      {stage === 'gate' && (
        <GateScreen mode="admin" error={error} onSubmit={login} />
      )}
      {(stage === 'splash' || stage === 'game') && (
        <Suspense fallback={<div data-testid="admin-loading-blank" />}>
          <AdminApp />
        </Suspense>
      )}
      <UpdateToast />
    </>
  );
}

function PlayerApp(): JSX.Element {
  const { stage, error, login, finishSplash } = useGateAuth();
  const content = useContent();
  // P5 UI-02: gate the game subtree on character choice. null == not-picked.
  // On confirm, CharacterSelect fires onSelected(id) which flips this to a
  // non-null value; the game subtree mounts and CharacterSelect unmounts.
  const [chosenCharacterId, setChosenCharacterId] = useState<string | null>(null);

  // P7 Plan 10 (UI-01, D-05): the title stage slots between splash and
  // CharacterSelect. `titleSeen` is advanced by the TitleScreen Play button;
  // CharacterSelect does not mount until the player presses Play. From the
  // title, the gear opens the SettingsPanel (from-title mode) and `How to play`
  // opens the ControlsTutorialCard in preview mode (neither touches a flag).
  const [titleSeen, setTitleSeen] = useState<boolean>(false);
  const [titleSettingsOpen, setTitleSettingsOpen] = useState<boolean>(false);
  const [tutorialPreviewOpen, setTutorialPreviewOpen] = useState<boolean>(false);

  // P7 Plan 10 (D-02): in-game pause UI. PauseButton emits `pause-requested`
  // ({source:'menu'}) which GameScene consumes to sleep the loop (Pitfall D: one
  // ticker, one pause). App mirrors that with the pause panel + (optional)
  // in-game SettingsPanel. The round-end beat (D-10) ALSO sleeps the loop, but
  // must show the RoundResultCard rather than the pause panel — `roundEndedRef`
  // gates that so a single bus contract drives both holds.
  const [pausePanelOpen, setPausePanelOpen] = useState<boolean>(false);
  const [inGameSettingsOpen, setInGameSettingsOpen] = useState<boolean>(false);
  // UI-05 (D-08/09): while the local player is caught, movement/action controls
  // hide (the camera follows a free hider; SpectatorBanner explains why).
  const [playerStatus, setPlayerStatus] = useState<EntityStatus | null>(null);
  const roundEndedRef = useRef<boolean>(false);

  // D-24 game-tree gate: stage=game AND content.ready. If splash finished
  // before the content fetch resolves, we re-show BootSplash with a no-op
  // onDone so the minimum-hold pattern absorbs the window. In practice this
  // is zero or near-zero on warm SW cache; useContent's fallback branch
  // guarantees ready flips true within milliseconds even on network failure.
  const contentReady = stage === 'game' && content.ready;
  const gameWaitingOnContent = stage === 'game' && !content.ready;
  // P7 Plan 10 (D-05): title → Play → CharacterSelect → game.
  const titleVisible = contentReady && !titleSeen;
  const characterSelectVisible =
    contentReady && titleSeen && chosenCharacterId === null;
  const gameReady = contentReady && chosenCharacterId !== null;

  // P7 Plan 10: game-subtree bus wiring. Gated on gameReady so the subscriptions
  // exist only while the game is mounted (and tear down cleanly on unmount).
  useEffect(() => {
    if (!gameReady) return;
    const offTick = renderBus.on('sim-tick', (e) => setPlayerStatus(e.playerStatus));
    // Round-end beat (D-10): hold the tableau (sleep the loop via the SAME seam
    // the PauseButton uses) and let the RoundResultCard show. The ref is set
    // BEFORE the synchronous emit so the pause-requested handler below knows not
    // to raise the pause panel for this hold.
    const offEnded = renderBus.on('round-ended', () => {
      roundEndedRef.current = true;
      renderBus.emit('pause-requested', { source: 'menu' });
    });
    const offStarted = renderBus.on('round-started', () => {
      roundEndedRef.current = false;
    });
    // An explicit menu pause (PauseButton) raises the pause panel — but not the
    // round-end hold, and not a tab-background (source:'visibility') auto-pause.
    const offPause = renderBus.on('pause-requested', (e) => {
      if (e.source === 'menu' && !roundEndedRef.current) setPausePanelOpen(true);
    });
    return (): void => {
      offTick();
      offEnded();
      offStarted();
      offPause();
    };
  }, [gameReady]);

  // RoundResultCard CTA → resume the loop so the round-FSM advances through the
  // diegetic team-switch into the next round (the FSM owns repositioning).
  const startNextRound = useCallback((): void => {
    roundEndedRef.current = false;
    renderBus.emit('resume-requested', { source: 'menu' });
  }, []);

  // Pause panel Resume / backdrop → resume the loop and clear the pause UI.
  const resumeFromPause = useCallback((): void => {
    renderBus.emit('resume-requested', { source: 'menu' });
    setPausePanelOpen(false);
  }, []);

  // In-game SettingsPanel close: the panel itself emits `resume-requested`
  // (inGame contract), so App only clears its overlay state — resuming the game
  // and dismissing the pause stack in one go (07-UI-SPEC §Surface 2/3).
  const closeInGameSettings = useCallback((): void => {
    setInGameSettingsOpen(false);
    setPausePanelOpen(false);
  }, []);

  const spectating = playerStatus === 'caught';

  // P6 D-18: returning-session iOS audio unlock probe. After audioManager.init()
  // settles (which only runs once gameReady && content.ready, so the
  // ContentBundle is hydrated), wait P6_UNLOCK_BOOT_PROBE_MS then check
  // Howler's AudioContext state. If it is NOT running, mount the
  // <AudioUnlockSplash /> so the user's next touchend can resume the ctx.
  // null = not yet probed; false = probe says no splash needed; true =
  // splash is mounted and waiting for dismissal.
  const [needsUnlockSplash, setNeedsUnlockSplash] = useState<boolean | null>(null);

  useEffect(() => {
    if (!gameReady) return;
    // P7 Plan 10 (UI-06, Pitfall E): read the persisted `hunter:settings` blob
    // and apply the audio bus gains BEFORE the game subtree mounts / any Howl
    // plays. audioManager.init() (below) also seeds from the same blob; applying
    // here makes the contract explicit and testable, and keeps the settings
    // store (self-hydrated at import — the Joystick reads getSettings() for its
    // size) the single source of truth. Fail-open: loadSettings never throws.
    const settings = loadSettings();
    audioManager.setVolumes({
      master: settings.master,
      music: settings.music,
      sfx: settings.sfx,
    });
    // [Rule 1 - Bug] audioManager.init() is async and can reject (e.g.
    // jsdom-shaped fetch URL rejection inside Vitest, where the manifest
    // path is relative; production has window.location.origin to anchor
    // the URL). Swallow the rejection at the call site so a transient
    // boot-time fetch failure cannot bubble up as an unhandled rejection
    // and break unrelated UI tests. The user-visible fallback is the
    // ambient bed simply not playing yet — playback resumes on next
    // user gesture / page reload.
    audioManager.init().catch(() => {
      /* swallowed — manager stays initialized=false and can be retried */
    });
    // P6-Pitfall G: defensive boot probe. Use the literal 'window.Howler'
    // global if present (Howler attaches itself there in browser builds);
    // otherwise fall back to assuming unlocked (avoids false-positive
    // mounting of the splash on test/SSR shells). The cast to `unknown`
    // first satisfies the strict no-explicit-any rule for a one-off probe.
    const t = window.setTimeout(() => {
      const w = window as unknown as {
        Howler?: { ctx?: { state?: string } | null };
      };
      const ctxRunning = w.Howler?.ctx?.state === 'running';
      setNeedsUnlockSplash(!ctxRunning);
    }, P6_UNLOCK_BOOT_PROBE_MS);
    return () => window.clearTimeout(t);
  }, [gameReady]);

  return (
    <>
      {/* Self-gated via isPreviewMode(); mounted at top level so the banner
       *  is visible across every stage (UI-SPEC "non-dismissable non-transient"). */}
      <PreviewBanner />
      {stage === 'loading' && (
        <div
          data-testid="loading-blank"
          style={{
            background: '#0d1f2d',
            position: 'fixed',
            inset: 0,
          }}
        />
      )}
      {stage === 'gate' && <GateScreen error={error} onSubmit={login} />}
      {stage === 'splash' && <BootSplash onDone={finishSplash} />}
      {/* P5 D-24: stage==='game' but content still loading — hold the splash.
       *  onDone is a no-op: we wait on content.ready, not on a new timer. */}
      {gameWaitingOnContent && <BootSplash onDone={() => { /* hold until content ready */ }} />}
      {/* P7 Plan 10 (UI-01, D-05): title stage. Play advances to CharacterSelect;
       *  the gear opens the from-title SettingsPanel; `How to play` opens the
       *  ControlsTutorialCard in preview mode (the seen-flag is untouched). */}
      {titleVisible && (
        <TitleScreen
          onPlay={(): void => setTitleSeen(true)}
          onHowTo={(): void => setTutorialPreviewOpen(true)}
          onSettings={(): void => setTitleSettingsOpen(true)}
        />
      )}
      {titleVisible && titleSettingsOpen && (
        <SettingsPanel
          inGame={false}
          onClose={(): void => setTitleSettingsOpen(false)}
        />
      )}
      {titleVisible && tutorialPreviewOpen && (
        <ControlsTutorialCard
          preview
          onDismiss={(): void => setTutorialPreviewOpen(false)}
        />
      )}
      {/* P5 UI-02: CharacterSelect mounts between content.ready and game subtree.
       *  Game cannot boot until the player picks a character — the confirm
       *  handler flips chosenCharacterId and also writes sessionStorage so
       *  GameScene reads the seeded value at scene init. */}
      {characterSelectVisible && <CharacterSelect onSelected={setChosenCharacterId} />}
      {gameReady && (
        <>
          <PhaserGame />
          <InstallHint />
          {/* UI-05 (D-08/09): movement + action controls hide while the player
           *  is caught/spectating — the camera follows a free hider and the
           *  SpectatorBanner explains the loss of control. PauseButton stays. */}
          {!spectating && (
            <>
              <Joystick />   {/* nipplejs dynamic-mode joystick. Mounts on unlock, unmounts on lock. */}
              {/* P2 hunter-only torch toggle (role-gated in the component). */}
              <TorchButton />
              <HideExitButton />
              <OnFloorDropButton />
            </>
          )}
          {/* P3 Plan 03-08 HUD overlays. Each component self-gates visibility via
           *  the sim-tick payload (role/status/phase/ability) so mounting them
           *  unconditionally here is safe. */}
          <RoundTimer />
          <HeadstartCountdown />
          <StaminaBar />
          <TeamSwitchOverlay />
          {/* P6 Plan 04: BubbleLayer subsumes the legacy FoxguardBubble — it
           *  subscribes to 'foxguard-called' as a critical-tier source
           *  (foxguard-styled border) plus all character-spoke / adult /
           *  catch / free-all / shout / fear / dog events. Cap-3 preempt. */}
          <BubbleLayer />
          {/* P6 Plan 07 (FEAR-07): the invisible half of the cue layer.
           *  Vignette opacity / heartbeat BPM / sprite shiver continuously
           *  scale off the local player's Entity.fear (D-13/14/15/16). The
           *  CSS layer self-gates on prefers-reduced-motion; the JS gates the
           *  shiver and visual layers but lets the heartbeat audio play. */}
          <FearFeedbackLayer />
          {/* P4 Plan 01 scaffold: RoundSetup renders null until Plan 09 adds drag-drop UI (D-37). */}
          <RoundSetup />
          {/* P7 Plan 10 surfaces. PauseButton (HUD, always visible — including
           *  while spectating) emits the in-game pause signal. SpectatorBanner +
           *  RoundResultCard self-gate off the bus. ControlsTutorialCard
           *  (non-preview) self-gates on the `hunter:tutorial.seen` flag so it
           *  shows once before the first round and never again. */}
          <PauseButton />
          <SpectatorBanner />
          <RoundResultCard onNextRound={startNextRound} />
          <ControlsTutorialCard onDismiss={(): void => { /* component owns the seen-flag */ }} />
          {/* In-game pause panel (z-185): Paused + Resume + Settings over a
           *  dim+blur. Suppressed during the round-end hold (RoundResultCard
           *  owns that beat). Backdrop tap / Resume resumes the loop. */}
          {pausePanelOpen && !inGameSettingsOpen && (
            <div
              className="pause-panel"
              data-testid="pause-panel-backdrop"
              // Use onPointerDown instead of onClick so that the touch
              // sequence that OPENED the panel (pointerdown on PauseButton →
              // panel renders → iOS synthesises a click on this backdrop) does
              // NOT immediately dismiss it. With onPointerDown: the originating
              // pointerdown fires on the PauseButton (not the backdrop, which
              // didn't exist yet), so the backdrop's dismiss only fires on a
              // SUBSEQUENT touch — the user genuinely tapping the dim area.
              // Debug session p11-uat-round4-fixes (2026-07-05).
              onPointerDown={resumeFromPause}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 185,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                background: 'rgba(13, 31, 45, 0.7)',
                backdropFilter: 'blur(12px)',
                color: '#f0ead8',
                fontFamily: 'ui-sans-serif, system-ui, sans-serif',
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Paused"
                data-testid="pause-panel"
                // Stop both click AND pointerdown from bubbling to the
                // backdrop so tapping the dialog content doesn't dismiss the
                // panel. Resume/Settings buttons inside the dialog use onClick
                // which is unaffected by pointerdown stopPropagation.
                onClick={(e): void => e.stopPropagation()}
                onPointerDown={(e): void => e.stopPropagation()}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <h2 style={{ margin: 0, fontSize: '36px', fontWeight: 700 }}>Paused</h2>
                <button
                  type="button"
                  data-testid="pause-resume"
                  onClick={resumeFromPause}
                  style={{
                    minWidth: '200px',
                    minHeight: '56px',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#e8a838',
                    color: '#0d1f2d',
                    fontSize: '18px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Resume
                </button>
                <button
                  type="button"
                  data-testid="pause-settings"
                  onClick={(): void => setInGameSettingsOpen(true)}
                  style={{
                    minWidth: '200px',
                    minHeight: '44px',
                    borderRadius: '12px',
                    border: '2px solid #2a4a62',
                    background: 'transparent',
                    color: '#f0ead8',
                    fontSize: '16px',
                    cursor: 'pointer',
                  }}
                >
                  Settings
                </button>
              </div>
            </div>
          )}
          {/* In-game SettingsPanel (z-190) over the paused canvas. Its close path
           *  emits `resume-requested` itself (inGame contract); App clears the
           *  pause stack so the game resumes. */}
          {inGameSettingsOpen && (
            <SettingsPanel inGame onClose={closeInGameSettings} />
          )}
        </>
      )}
      {/* P6 D-18: returning-session iOS unlock splash. Only mounts when the
       *  boot probe found Howler.ctx.state !== 'running' AFTER audioManager
       *  finished init. Touch/click → audioManager.unlock() → 300ms fade →
       *  unmount. Sits between game subtree and overlays so it covers the
       *  Phaser canvas during the brief unlock moment. */}
      {needsUnlockSplash === true && (
        <AudioUnlockSplash
          onDismissed={(): void => setNeedsUnlockSplash(false)}
        />
      )}
      {/* Overlays always present so update-available can fire on any stage */}
      <VersionOverlay />
      <DevOverlay />
      <UpdateToast />
    </>
  );
}
